import { useEffect, useRef, useState } from 'react';
const marker = '__cashNavigation';
/** Memory owns UI state; browser history contains only an opaque session and index. */
export function useCashNavigation({ value, signature, boundary, restore, guard, closeDialog, hostBlocked, position, restorePosition, }) {
    const live = useRef({
        value,
        signature,
        restore,
        guard,
        closeDialog,
        hostBlocked,
        position,
        restorePosition,
    });
    live.current = {
        value,
        signature,
        restore,
        guard,
        closeDialog,
        hostBlocked,
        position,
        restorePosition,
    };
    const memory = useRef({
        token: '',
        boundary,
        index: 0,
        base: 0,
        owned: new Set(),
        frames: new Map(),
        restoring: false,
        rollback: false,
    });
    const [, render] = useState(0);
    useEffect(() => {
        const state = memory.current;
        if (!state.token || state.boundary !== boundary) {
            state.base += state.index;
            state.token = `${Date.now()}-${Math.random()}`;
            state.owned.add(state.token);
            state.boundary = boundary;
            state.index = 0;
            state.frames.clear();
            state.restoring = false;
            state.rollback = false;
            state.accept = undefined;
            state.prune = undefined;
            state.afterRollback = undefined;
            history.replaceState({ ...history.state, [marker]: { token: state.token, index: 0, position: state.base } }, '');
        }
        const previous = state.frames.get(state.index);
        if (state.restoring)
            state.restoring = false;
        else if (previous && previous.signature !== signature) {
            for (const index of state.frames.keys())
                if (index > state.index)
                    state.frames.delete(index);
            state.index++;
            history.pushState({
                ...history.state,
                [marker]: { token: state.token, index: state.index, position: state.base + state.index },
            }, '');
        }
        state.frames.set(state.index, {
            value,
            signature,
            position: previous?.position ?? live.current.position(),
        });
    }, [value, signature, boundary]);
    useEffect(() => {
        const pop = (event) => {
            const state = memory.current, destination = event.state?.[marker];
            if (state.rollback) {
                state.rollback = false;
                event.stopImmediatePropagation();
                const work = state.afterRollback;
                state.afterRollback = undefined;
                work?.();
                return;
            }
            const internal = destination?.token === state.token && state.frames.has(destination.index);
            if (!internal &&
                !state.owned.has(destination?.token) &&
                !live.current.hostBlocked &&
                !document.querySelector('dialog[open]'))
                return;
            event.stopImmediatePropagation();
            const target = internal ? destination.index : undefined;
            const delta = target === undefined
                ? typeof destination?.position === 'number'
                    ? state.base + state.index - destination.position
                    : 1
                : state.index - target;
            // Put browser/host back before asking the current screen's leave guard.
            if (state.accept !== target || target === undefined) {
                state.rollback = true;
                history.go(delta || 1);
                if (live.current.closeDialog())
                    return;
                if (target === undefined)
                    return;
                live.current.guard(() => {
                    const apply = () => {
                        state.accept = target;
                        history.go(target - state.index);
                    };
                    if (state.rollback)
                        state.afterRollback = apply;
                    else
                        apply();
                });
                return;
            }
            state.accept = undefined;
            const frame = state.frames.get(target);
            if (!frame)
                return;
            state.index = target;
            state.restoring = true;
            if (state.prune !== undefined) {
                for (const index of state.frames.keys())
                    if (index > state.prune)
                        state.frames.delete(index);
                state.prune = undefined;
            }
            live.current.restore(frame.value);
            live.current.restorePosition(frame.position);
            render(count => count + 1);
        };
        const capture = () => {
            const state = memory.current, frame = state.frames.get(state.index);
            if (frame && frame.signature === live.current.signature && !document.querySelector('dialog[open]')) {
                const next = live.current.position();
                frame.position = { scroll: next.scroll, focus: next.focus || frame.position.focus };
            }
        };
        window.addEventListener('popstate', pop, true);
        window.addEventListener('scroll', capture, { passive: true });
        document.addEventListener('pointerdown', capture, true);
        document.addEventListener('keydown', capture, true);
        return () => {
            window.removeEventListener('popstate', pop, true);
            window.removeEventListener('scroll', capture);
            document.removeEventListener('pointerdown', capture, true);
            document.removeEventListener('keydown', capture, true);
        };
    }, []);
    const frames = [...memory.current.frames.entries()]
        .filter(([index, frame]) => memory.current.boundary === boundary &&
        (index < memory.current.index ||
            (index === memory.current.index && frame.signature !== signature)))
        .sort(([a], [b]) => b - a)
        .map(([index, frame]) => ({ index, value: frame.value }));
    const returnTo = (index) => live.current.guard(() => {
        const state = memory.current;
        if (!state.frames.has(index) || index === state.index)
            return;
        state.accept = index;
        state.prune = index;
        history.go(index - state.index);
    });
    // A known command receipt may resolve the current entity to a new identity.
    // Replace that frame synchronously, retaining its original Back destination.
    const replaceCurrent = (work) => {
        memory.current.restoring = true;
        work();
    };
    return { frames, returnTo, replaceCurrent };
}
